import { startTransition, useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  APICreateProjectDocument,
  APIArchiveProject,
  APICreateProjectIssue,
  APICreateProject,
  APIDeleteProjectDocument,
  APIDeleteProjectIssue,
  APIGetProjects,
  APITransitionProject,
  APIUpdateProjectDocument,
  APIUpdateProjectIssue,
  APIUpdateProject,
  CatalystRow,
  CreateProjectPayload,
  ProjectDocument,
  ProjectDocumentPayload,
  PROJECT_HEALTHS,
  PROJECT_STAGES,
  ProjectIssuePayload,
  ProjectHealthFilter,
  ProjectListQuery,
  ProjectListResponse,
  QualityIssue,
  ProjectSortDirection,
  ProjectSortField,
  ProjectStageFilter,
  SessionInfo,
  TransitionProjectPayload,
  UpdateProjectPayload,
} from '../../services/api.service';
import { useAuth } from '../../store/AuthContext';
import ProjectEditorDialog, {
  createEmptyProjectDraft,
  ProjectEditorValues,
} from './ProjectEditorDialog';
import ProjectViewDialog from './ProjectViewDialog';
import './ProjectRegisterPage.css';

type EditorMode = 'create' | 'edit';
type NoticeTone = 'error' | 'success';
type SortableColumn = Extract<
  ProjectSortField,
  'name' | 'client' | 'owner' | 'stage' | 'health' | 'updatedAt'
>;

interface EditorState {
  mode: EditorMode;
  project: null | CatalystRow;
}

interface CatalystUser {
  email_id?: string;
  first_name?: string;
}

const DEFAULT_QUERY: ProjectListQuery = {
  page: 1,
  limit: 10,
  search: '',
  stage: 'all',
  health: 'all',
  sortBy: 'updatedAt',
  sortOrder: 'desc',
};

const SORTABLE_HEADERS: Array<{ field: SortableColumn; label: string }> = [
  { field: 'name', label: 'Project' },
  { field: 'client', label: 'Client' },
  { field: 'owner', label: 'Owner' },
  { field: 'stage', label: 'Stage' },
  { field: 'health', label: 'Health' },
  { field: 'updatedAt', label: 'Updated' },
];

function formatDate(value?: string): string {
  if (!value) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatRange(pagination: ProjectListResponse['pagination']): string {
  if (pagination.totalRecords === 0) {
    return 'No records';
  }

  const startRecord = (pagination.page - 1) * pagination.limit + 1;
  const endRecord = Math.min(
    pagination.page * pagination.limit,
    pagination.totalRecords
  );

  return `${startRecord}-${endRecord} of ${pagination.totalRecords}`;
}

function buildPageItems(currentPage: number, totalPages: number) {
  if (totalPages <= 1) {
    return [1];
  }

  const pageItems = new Set<number>();
  pageItems.add(1);
  pageItems.add(totalPages);

  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page >= 1 && page <= totalPages) {
      pageItems.add(page);
    }
  }

  return Array.from(pageItems).sort((left, right) => left - right);
}

function buildDraft(project: null | CatalystRow): ProjectEditorValues {
  if (!project) {
    return createEmptyProjectDraft();
  }

  return {
    name: project.name,
    client: project.client,
    owner: project.owner,
    stage: project.stage,
    health: project.health,
    summary: project.summary ?? '',
    nextAction: project.nextAction ?? '',
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function getNextSortDirection(
  field: SortableColumn,
  activeField: ProjectSortField,
  activeDirection: ProjectSortDirection
): ProjectSortDirection {
  if (activeField !== field) {
    return field === 'updatedAt' ? 'desc' : 'asc';
  }

  return activeDirection === 'asc' ? 'desc' : 'asc';
}

function getSortIndicator(
  field: SortableColumn,
  activeField: ProjectSortField,
  activeDirection: ProjectSortDirection
) {
  if (field !== activeField) {
    return '↕';
  }

  return activeDirection === 'asc' ? '↑' : '↓';
}

export default function ProjectRegisterPage() {
  const { logout, user } = useAuth();
  const authenticatedUser = user as CatalystUser | null;
  const [query, setQuery] = useState<ProjectListQuery>(DEFAULT_QUERY);
  const [searchInput, setSearchInput] = useState('');
  const deferredSearch = useDeferredValue(searchInput);
  const [response, setResponse] = useState<null | ProjectListResponse>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [editorState, setEditorState] = useState<null | EditorState>(null);
  const [editorBusy, setEditorBusy] = useState(false);
  const [editorError, setEditorError] = useState('');
  const [busyProjectId, setBusyProjectId] = useState<null | string>(null);
  const [session, setSession] = useState<null | SessionInfo>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [viewProject, setViewProject] = useState<null | CatalystRow>(null);
  const [notice, setNotice] = useState<null | {
    message: string;
    tone: NoticeTone;
  }>(null);

  function showNotice(message: string, tone: NoticeTone) {
    setNotice({ message, tone });
  }

  function syncProjectState(project: CatalystRow) {
    setViewProject(project);
    setResponse((currentResponse) => {
      if (!currentResponse) {
        return currentResponse;
      }

      const nextData = currentResponse.data.map((row) =>
        row._id === project._id ? project : row
      );

      return {
        ...currentResponse,
        data: nextData,
      };
    });
  }

  function patchQuery(
    patch: Partial<ProjectListQuery>,
    options?: { resetPage?: boolean }
  ) {
    startTransition(() => {
      setQuery((currentQuery) => ({
        ...currentQuery,
        ...patch,
        page:
          patch.page ??
          (options?.resetPage ? 1 : currentQuery.page ?? DEFAULT_QUERY.page),
      }));
    });
  }

  useEffect(() => {
    const nextSearchValue = deferredSearch.trim();
    setQuery((currentQuery) => {
      if (nextSearchValue === (currentQuery.search ?? '')) {
        return currentQuery;
      }

      return {
        ...currentQuery,
        search: nextSearchValue,
        page: 1,
      };
    });
  }, [deferredSearch]);

  useEffect(() => {
    let ignore = false;

    async function loadProjects() {
      setLoading(true);
      setErrorMessage('');

      try {
        const result = await APIGetProjects(query);
        if (ignore) {
          return;
        }

        setResponse(result.data);
        if (result.data.session) {
          setSession(result.data.session);
        }

        if (result.data.pagination.page !== query.page) {
          setQuery((currentQuery) => ({
            ...currentQuery,
            page: result.data.pagination.page,
          }));
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(getErrorMessage(error, 'Could not load the register.'));
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadProjects();

    return () => {
      ignore = true;
    };
  }, [query, refreshToken]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setNotice(null);
    }, 3200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [notice]);

  useEffect(() => {
    if (!response?.data.length || !viewProject) {
      return;
    }

    const refreshedProject = response.data.find(
      (project) => project._id === viewProject._id
    );

    if (refreshedProject && refreshedProject !== viewProject) {
      setViewProject(refreshedProject);
    }
  }, [response?.data, viewProject]);

  const overallSummary = response?.summary.overall;
  const dashboard = response?.dashboard;
  const pagination = response?.pagination;
  const pageItems = buildPageItems(
    pagination?.page ?? 1,
    pagination?.totalPages ?? 1
  );
  const hasActiveFilters = Boolean(
    (query.search ?? '').trim() ||
      (query.stage && query.stage !== 'all') ||
      (query.health && query.health !== 'all')
  );
  const activeSortField = (query.sortBy ?? DEFAULT_QUERY.sortBy) as ProjectSortField;
  const activeSortDirection =
    (query.sortOrder ?? DEFAULT_QUERY.sortOrder) as ProjectSortDirection;
  const currentProjectCount = useMemo(
    () => response?.data.length ?? 0,
    [response?.data.length]
  );
  const canManage = session?.canManage ?? false;
  const roleLabel = session?.role === 'admin' ? 'Manager' : 'Viewer';

  async function handleTransition(
    project: CatalystRow,
    payload: TransitionProjectPayload
  ) {
    setTransitioning(true);

    try {
      const result = await APITransitionProject(project._id, payload);
      syncProjectState(result.data.data);
      showNotice('Workflow updated.', 'success');
      setRefreshToken((currentToken) => currentToken + 1);
    } catch (error) {
      showNotice(
        getErrorMessage(error, 'Could not update the workflow.'),
        'error'
      );
    } finally {
      setTransitioning(false);
    }
  }

  async function handleSaveProject(values: ProjectEditorValues) {
    if (!editorState) {
      return;
    }

    const payload: CreateProjectPayload | UpdateProjectPayload = {
      name: values.name,
      client: values.client,
      owner: values.owner,
      summary: values.summary,
      nextAction: values.nextAction,
    };

    setEditorBusy(true);
    setEditorError('');

    try {
      let savedProject: CatalystRow | null = null;

      if (editorState.mode === 'create') {
        const result = await APICreateProject(payload as CreateProjectPayload);
        savedProject = result.data.data;
        showNotice('Project created.', 'success');
      } else if (editorState.project) {
        const result = await APIUpdateProject(
          editorState.project._id,
          payload as UpdateProjectPayload
        );
        savedProject = result.data.data;
        showNotice('Project updated.', 'success');
      }

      if (savedProject) {
        syncProjectState(savedProject);
      }

      setEditorState(null);
      setRefreshToken((currentToken) => currentToken + 1);
    } catch (error) {
      setEditorError(getErrorMessage(error, 'Could not save the project.'));
    } finally {
      setEditorBusy(false);
    }
  }

  async function handleArchive(project: CatalystRow) {
    const confirmed = window.confirm(
      `Archive ${project.projectCode} - ${project.name}?`
    );

    if (!confirmed) {
      return;
    }

    setBusyProjectId(project._id);

    try {
      await APIArchiveProject(project._id);
      if (viewProject?._id === project._id) {
        setViewProject(null);
      }
      showNotice('Project archived.', 'success');
      setRefreshToken((currentToken) => currentToken + 1);
    } catch (error) {
      showNotice(getErrorMessage(error, 'Could not archive the project.'), 'error');
    } finally {
      setBusyProjectId(null);
    }
  }

  async function handleCreateIssue(
    project: CatalystRow,
    payload: ProjectIssuePayload
  ) {
    const result = await APICreateProjectIssue(project._id, payload);
    syncProjectState(result.data.data);
    showNotice('Quality issue added.', 'success');
    setRefreshToken((currentToken) => currentToken + 1);
  }

  async function handleUpdateIssue(
    project: CatalystRow,
    issue: QualityIssue,
    payload: Partial<ProjectIssuePayload>
  ) {
    const result = await APIUpdateProjectIssue(project._id, issue._id, payload);
    syncProjectState(result.data.data);
    showNotice('Quality issue updated.', 'success');
    setRefreshToken((currentToken) => currentToken + 1);
  }

  async function handleDeleteIssue(project: CatalystRow, issue: QualityIssue) {
    const confirmed = window.confirm(`Remove issue "${issue.title}"?`);
    if (!confirmed) {
      return;
    }

    const result = await APIDeleteProjectIssue(project._id, issue._id);
    syncProjectState(result.data.data);
    showNotice('Quality issue removed.', 'success');
    setRefreshToken((currentToken) => currentToken + 1);
  }

  async function handleCreateDocument(
    project: CatalystRow,
    payload: ProjectDocumentPayload
  ) {
    const result = await APICreateProjectDocument(project._id, payload);
    syncProjectState(result.data.data);
    showNotice('Document added.', 'success');
    setRefreshToken((currentToken) => currentToken + 1);
  }

  async function handleUpdateDocument(
    project: CatalystRow,
    document: ProjectDocument,
    payload: Partial<ProjectDocumentPayload>
  ) {
    const result = await APIUpdateProjectDocument(
      project._id,
      document._id,
      payload
    );
    syncProjectState(result.data.data);
    showNotice('Document updated.', 'success');
    setRefreshToken((currentToken) => currentToken + 1);
  }

  async function handleDeleteDocument(
    project: CatalystRow,
    document: ProjectDocument
  ) {
    const confirmed = window.confirm(`Remove document "${document.title}"?`);
    if (!confirmed) {
      return;
    }

    const result = await APIDeleteProjectDocument(project._id, document._id);
    syncProjectState(result.data.data);
    showNotice('Document removed.', 'success');
    setRefreshToken((currentToken) => currentToken + 1);
  }

  function openEditor(mode: EditorMode, project: null | CatalystRow = null) {
    setEditorError('');
    setEditorState({ mode, project });
  }

  function handleSort(field: SortableColumn) {
    patchQuery(
      {
        sortBy: field,
        sortOrder: getNextSortDirection(
          field,
          activeSortField,
          activeSortDirection
        ),
      },
      { resetPage: true }
    );
  }

  return (
    <div className="register-page">
      <ProjectEditorDialog
        busy={editorBusy}
        errorMessage={editorError}
        initialValues={buildDraft(editorState?.project ?? null)}
        mode={editorState?.mode ?? 'create'}
        open={Boolean(editorState)}
        onClose={() => setEditorState(null)}
        onSubmit={handleSaveProject}
      />

      <ProjectViewDialog
        busy={busyProjectId === viewProject?._id}
        canManage={canManage}
        open={Boolean(viewProject)}
        project={viewProject}
        transitioning={transitioning}
        onArchive={handleArchive}
        onClose={() => setViewProject(null)}
        onCreateDocument={handleCreateDocument}
        onCreateIssue={handleCreateIssue}
        onDeleteDocument={handleDeleteDocument}
        onDeleteIssue={handleDeleteIssue}
        onEdit={(project) => openEditor('edit', project)}
        onTransition={handleTransition}
        onUpdateDocument={handleUpdateDocument}
        onUpdateIssue={handleUpdateIssue}
      />

      <header className="register-header">
        <div className="register-header__leading">
          <button
            aria-label="Toggle menu"
            className="icon-button icon-button--menu"
            type="button"
            onClick={() => setMobileSidebarOpen((currentValue) => !currentValue)}
          >
            Menu
          </button>
          <div>
            <p className="register-header__eyebrow">Eternia</p>
            <h1>Eternia Project Management Portal</h1>
          </div>
        </div>

        <div className="register-header__actions">
          <div className="profile-chip">
            <span className="profile-chip__avatar">
              {(authenticatedUser?.first_name ??
                authenticatedUser?.email_id ??
                'U')
                .charAt(0)
                .toUpperCase()}
            </span>
            <div>
              <span className="profile-chip__label">Signed in</span>
              <strong>
                {authenticatedUser?.first_name ??
                  authenticatedUser?.email_id ??
                  'Catalyst user'}
              </strong>
            </div>
            {session ? (
              <span
                className={
                  canManage
                    ? 'role-badge role-badge--manager'
                    : 'role-badge role-badge--viewer'
                }
              >
                {roleLabel}
              </span>
            ) : null}
          </div>

          <button className="ghost-button" type="button" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <div
        aria-hidden={!mobileSidebarOpen}
        className={
          mobileSidebarOpen ? 'sidebar-scrim sidebar-scrim--open' : 'sidebar-scrim'
        }
        onClick={() => setMobileSidebarOpen(false)}
      />

      <aside
        className={
          mobileSidebarOpen
            ? 'register-sidebar register-sidebar--open'
            : 'register-sidebar'
        }
      >
        <div className="register-sidebar__section">
          <p className="section-label">Navigation</p>

          <button
            className="nav-item nav-item--active"
            type="button"
            onClick={() => {
              setMobileSidebarOpen(false);
              setSearchInput('');
              setQuery(DEFAULT_QUERY);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <span>Projects</span>
            <strong>{overallSummary?.totalRecords ?? 0}</strong>
          </button>
        </div>
      </aside>

      <main className="register-main">
        {notice ? (
          <div className={`banner banner--${notice.tone}`}>
            <span>{notice.message}</span>
            <button className="ghost-button" type="button" onClick={() => setNotice(null)}>
              Dismiss
            </button>
          </div>
        ) : null}

        <section className="workspace-heading">
          <div>
            <p className="section-label">Projects workspace</p>
            <h2>Project register</h2>
            <p>Track workflow, quality, and evidence from one register.</p>
          </div>

          <div className="workspace-heading__meta">
            <span className="summary-chip">
              {overallSummary?.totalRecords ?? 0} live records
            </span>
            {canManage ? (
              <button
                className="primary-button"
                type="button"
                onClick={() => openEditor('create')}
              >
                New project
              </button>
            ) : null}
          </div>
        </section>

        <section className="overview-grid">
          <article className="overview-card">
            <span className="section-label">Active projects</span>
            <strong>{dashboard?.totalProjects ?? 0}</strong>
            <p>Current live portfolio in this Catalyst demo environment.</p>
          </article>

          <article className="overview-card overview-card--warn">
            <span className="section-label">Needs attention</span>
            <strong>{dashboard?.projectsNeedingAction ?? 0}</strong>
            <p>Projects with blocked health, missing evidence, or gate issues.</p>
          </article>

          <article className="overview-card">
            <span className="section-label">Open quality issues</span>
            <strong>{dashboard?.openIssues ?? 0}</strong>
            <p>
              {dashboard?.criticalIssues ?? 0} critical issues across all active
              projects.
            </p>
          </article>

          <article className="overview-card">
            <span className="section-label">Pending documents</span>
            <strong>{dashboard?.pendingDocuments ?? 0}</strong>
            <p>
              {dashboard?.warrantyProjects ?? 0} projects are already in warranty.
            </p>
          </article>
        </section>

        <section className="dataset-panel">
          <div className="toolbar-panel">
            <div className="toolbar-panel__filters">
              <label className="search-field">
                <span className="search-field__label">Search</span>
                <input
                  placeholder="Project, code, client, owner"
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </label>

              <label className="field">
                <span className="field__label">Stage</span>
                <select
                  value={query.stage ?? 'all'}
                  onChange={(event) =>
                    patchQuery(
                      { stage: event.target.value as ProjectStageFilter },
                      { resetPage: true }
                    )
                  }
                >
                  <option value="all">All stages</option>
                  {PROJECT_STAGES.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="field__label">Health</span>
                <select
                  value={query.health ?? 'all'}
                  onChange={(event) =>
                    patchQuery(
                      { health: event.target.value as ProjectHealthFilter },
                      { resetPage: true }
                    )
                  }
                >
                  <option value="all">All health</option>
                  {PROJECT_HEALTHS.map((health) => (
                    <option key={health} value={health}>
                      {health}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="toolbar-panel__actions">
              <span className="toolbar-panel__summary">
                Showing {currentProjectCount} records on this page
              </span>
              <button
                className="ghost-button"
                disabled={!hasActiveFilters}
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setQuery(DEFAULT_QUERY);
                }}
              >
                Clear filters
              </button>
            </div>
          </div>

          <header className="dataset-panel__header">
            <div>
              <p className="section-label">Records</p>
              <h2>Projects</h2>
            </div>
            <div className="dataset-panel__meta">
              <span>{pagination ? formatRange(pagination) : 'Loading...'}</span>
            </div>
          </header>

          {errorMessage ? (
            <div className="state-panel state-panel--error">
              <p>{errorMessage}</p>
            </div>
          ) : loading ? (
            <div className="state-panel">
              <p>Loading projects...</p>
            </div>
          ) : !response?.data.length ? (
            <div className="state-panel">
              <p>No projects matched this view.</p>
            </div>
          ) : (
            <div className="table-shell">
              <table className="project-table">
                <thead>
                  <tr>
                    {SORTABLE_HEADERS.map(({ field, label }) => (
                      <th key={field}>
                        <button
                          className={
                            field === activeSortField
                              ? 'table-sort table-sort--active'
                              : 'table-sort'
                          }
                          type="button"
                          onClick={() => handleSort(field)}
                        >
                          <span>{label}</span>
                          <span className="table-sort__icon">
                            {getSortIndicator(
                              field,
                              activeSortField,
                              activeSortDirection
                            )}
                          </span>
                        </button>
                      </th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {response.data.map((project) => {
                    const rowBusy = busyProjectId === project._id;
                    const isOpen = viewProject?._id === project._id;

                    return (
                      <tr
                        key={project._id}
                        className={
                          isOpen
                            ? 'project-table__row project-table__row--selected'
                            : 'project-table__row'
                        }
                        onClick={() => setViewProject(project)}
                      >
                        <td>
                          <div className="project-primary">
                            <strong>{project.name}</strong>
                            <span>{project.projectCode}</span>
                            <small className="project-row-meta">
                              {project.workflow?.nextStage
                                ? project.workflow.canAdvance
                                  ? `Ready for ${project.workflow.nextStage}`
                                  : `${project.workflowSummary?.openIssues ?? 0} open issues · ${
                                      project.workflowSummary?.documentCoverage.pending ?? 0
                                    } pending docs`
                                : 'Workflow complete'}
                            </small>
                          </div>
                        </td>
                        <td>{project.client}</td>
                        <td>{project.owner}</td>
                        <td>
                          <span className="pill pill--neutral">{project.stage}</span>
                        </td>
                        <td>
                          <span
                            className={`pill pill--health-${project.health
                              .toLowerCase()
                              .replace(/\s+/g, '-')}`}
                          >
                            {project.health}
                          </span>
                        </td>
                        <td>{formatDate(project.updatedAt)}</td>
                        <td>
                          <div className="row-actions">
                            <button
                              className="link-button"
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setViewProject(project);
                              }}
                            >
                              View
                            </button>
                            {canManage ? (
                              <>
                                <button
                                  className="link-button"
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEditor('edit', project);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="link-button link-button--danger"
                                  disabled={rowBusy}
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void handleArchive(project);
                                  }}
                                >
                                  {rowBusy ? 'Archiving...' : 'Archive'}
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <footer className="paginator-bar">
            <div className="paginator-bar__report">
              {pagination ? formatRange(pagination) : 'Loading...'}
            </div>

            <div className="paginator-bar__controls">
              <button
                className="icon-button"
                disabled={!pagination?.hasPreviousPage}
                type="button"
                onClick={() => patchQuery({ page: 1 })}
              >
                First
              </button>
              <button
                className="icon-button"
                disabled={!pagination?.hasPreviousPage}
                type="button"
                onClick={() =>
                  patchQuery({ page: Math.max(1, (pagination?.page ?? 1) - 1) })
                }
              >
                Prev
              </button>

              {pageItems.map((pageItem) => (
                <button
                  key={pageItem}
                  className={
                    pagination?.page === pageItem
                      ? 'page-link page-link--active'
                      : 'page-link'
                  }
                  type="button"
                  onClick={() => patchQuery({ page: pageItem })}
                >
                  {pageItem}
                </button>
              ))}

              <button
                className="icon-button"
                disabled={!pagination?.hasNextPage}
                type="button"
                onClick={() =>
                  patchQuery({
                    page: Math.min(
                      pagination?.totalPages ?? 1,
                      (pagination?.page ?? 1) + 1
                    ),
                  })
                }
              >
                Next
              </button>
              <button
                className="icon-button"
                disabled={!pagination?.hasNextPage}
                type="button"
                onClick={() => patchQuery({ page: pagination?.totalPages ?? 1 })}
              >
                Last
              </button>
            </div>

            <label className="field field--compact paginator-bar__rows">
              <span className="field__label">Rows</span>
              <select
                value={query.limit}
                onChange={(event) =>
                  patchQuery(
                    { limit: Number(event.target.value) },
                    { resetPage: true }
                  )
                }
              >
                {[5, 10, 20, 30].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </footer>
        </section>
      </main>
    </div>
  );
}
